import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { useCreateMessageMutation } from "messaging/_api/messaging-api";
import Forum from "messaging/forums/forum";
import Message from "messaging/message";
import UserInformation from "users/information/user-information";
import { useSession } from "users/__session/session-context";
import {
  useCreateChatForumForNetworkConnectionMutation,
  userNetworkConnectionsApi,
} from "users/networking/connections/_api/user-network-connection-api";
import { getNetworkConnectionChatForum } from "users/networking/connections/network-connection-chat-forum";

/**
 * Posts a message into the session user's chat with one of their contacts, starting that chat if
 * the two have never talked. Rejects when it cannot; the caller says so.
 *
 * The chat is looked up before it is created, every time: the create endpoint always makes a new
 * chat, so creating first would give the pair a second one each time something is sent.
 */
export default function useSendToNetworkContact(): (contact: UserInformation, content: string) => Promise<void> {
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const [createChat] = useCreateChatForumForNetworkConnectionMutation();
  const [createMessage] = useCreateMessageMutation();

  async function findOrStartChat(contact: UserInformation): Promise<Forum> {
    const existing = await dispatch(
      userNetworkConnectionsApi.endpoints.getChatForumForNetworkConnection.initiate(
        { userId: contact.id },
        { forceRefetch: true, subscribe: false },
      ),
    ).unwrap();
    const existingForum = getNetworkConnectionChatForum(existing);
    if (existingForum) return existingForum;

    if (!contact.connectionId) throw new Error(`${contact.name.fullName} is not a network contact.`);
    const started = getNetworkConnectionChatForum(await createChat({ connectionId: contact.connectionId }).unwrap());
    if (!started) throw new Error(`Couldn't start a chat with ${contact.name.fullName}.`);
    return started;
  }

  return async function send(contact: UserInformation, content: string): Promise<void> {
    if (!session.user) throw new Error("Must be signed in to send a message.");
    const forum = await findOrStartChat(contact);
    const message = Message.draft({ id: Guid.generate(), created: new AHDate(), forum, sender: session.user, content });
    message.publish();
    await createMessage({ message }).unwrap();
  };
}
